import React, { useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useBook } from '../contexts/BookContext';
import TutorialModal from '../components/TutorialSlideshow/TutorialModal';
import MinimizedDesignImageProgress from '../components/BookDesign/overlays/MinimizedDesignImageProgress';

export default function BookDesignTopNavLayout({ children }) {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const location = useLocation();
  const { hasActiveImageGeneration, pendingImageCount, pendingImageJobs, currentBook } = useBook();
  const [showImageGenWarning, setShowImageGenWarning] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  const handleBack = () => {
    // If we're in the design step, go to edit
    if (location.pathname.startsWith('/design/')) {
      navigate(`/edit/${bookId}`);
    } else {
      // For other steps, go back to design
      navigate(`/design/${bookId}`);
    }
  };

  const findSpreadWithPendingImages = useCallback(() => {
    if (pendingImageJobs.size === 0) return null;

    const firstJob = Array.from(pendingImageJobs.values())[0];
    return firstJob?.spreadId || null;
  }, [pendingImageJobs]);

  const handleWaitForImages = () => {
    const spreadWithPendingImages = findSpreadWithPendingImages();
    console.log('[BookDesignTopNavLayout] Wait for images clicked:', { spreadWithPendingImages, bookId });
    if (spreadWithPendingImages) {
      const spreadNumber = spreadWithPendingImages === 'spread-0' ? 0 : parseInt(spreadWithPendingImages.split('-')[1], 10);
      const navPath = `/design/${bookId}/${spreadNumber}`;
      console.log('[BookDesignTopNavLayout] Navigating to:', navPath);
      navigate(navPath);
    } else {
      console.log('[BookDesignTopNavLayout] No spread with pending images found');
    }
    setShowImageGenWarning(false);
  };

  return (
    <div className="min-h-[800px] flex flex-col">
      {/* Top-aligned navigation controls to match World step */}
      <div className="max-w-7xl mx-auto px-4 py-4 w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Design</h1>
            <p className="text-gray-600">Arrange pages and images, then refine your book’s look.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowTutorialModal(true)}
              className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
            >
              Tutorial
            </button>
            <button
              onClick={handleBack}
              className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-50"
            >
              Back to Story
            </button>
            <button
              onClick={() => {
                if (hasActiveImageGeneration) {
                  setShowImageGenWarning(true);
                  return;
                }
                navigate(`/print/${bookId}`);
              }}
              className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Continue to Print
            </button>
          </div>
        </div>
      </div>

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
        defaultTabId={4}
      />

      {/* Main content */}
      <div className="flex-1 min-h-[600px] relative">
        {children}
      </div>

      <MinimizedDesignImageProgress
        count={pendingImageCount}
        onClick={handleWaitForImages}
      />

      {showImageGenWarning && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowImageGenWarning(false)}
            />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Images Still Generating
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      One or more images are still being generated. Proceeding now may result in missing images in your final book.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 space-y-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={handleWaitForImages}
                >
                  Wait for Images
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => {
                    setShowImageGenWarning(false);
                    navigate(`/print/${bookId}`);
                  }}
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
